import {
    doc,
    serverTimestamp,
    writeBatch,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from '../../app/firebase/firebase.js';
import { appData, state } from '../../app/state/state.js';
import { handleDeleteItem } from '../../shared/api/parentItems.js';
import { openConfirmationModal } from '../../shared/ui/confirmationModal.js';
import { escapeHTML } from '../../shared/utils/contentUtils.js';
import { openChecklistTemplateForm } from './checklistTemplateForm.js';

const ENTITY_LABELS = {
    all: 'All boards',
    task: 'Tasks',
    patient: 'Patients',
    project: 'Projects',
    asset: 'Assets',
    lab: 'Labs',
    maintenance: 'Maintenance',
};

function attrValue(value = '') {
    return escapeHTML(String(value ?? ''));
}

function getOrderedTemplates() {
    return [...(appData.checklistTemplates || [])]
        .sort((a, b) => {
            const orderA = Number(a.sortOrder ?? 0);
            const orderB = Number(b.sortOrder ?? 0);
            if (orderA !== orderB) {return orderA - orderB;}
            return String(a.name || '').localeCompare(String(b.name || ''));
        });
}

function getTemplateOrder(template, index) {
    const order = Number(template?.sortOrder);
    return Number.isFinite(order) && order > 0 ? order : index + 1;
}

function formatAllowedEntities(allowedEntities = []) {
    if (!Array.isArray(allowedEntities) ||
        allowedEntities.length === 0 ||
        allowedEntities.includes('all')) {
        return ENTITY_LABELS.all;
    }
    return allowedEntities
        .map((entry) => ENTITY_LABELS[entry] || entry)
        .join(', ');
}

function getAllTypeOptions() {
    return [
        ...(appData.config?.taskTypes || []),
        ...(appData.config?.patientTypes || []),
        ...(appData.config?.labTypes || []),
        ...(appData.config?.projectTypes || []),
        ...(appData.config?.assetTypes || []),
        ...(appData.config?.maintenanceTypes || []),
    ];
}

function formatLinkedTypes(linkedTypes = []) {
    if (!Array.isArray(linkedTypes) || linkedTypes.length === 0) {
        return 'All types';
    }
    const allTypes = getAllTypeOptions();
    return linkedTypes
        .map((typeId) => {
            const found = allTypes.find((type) => String(type.id) === String(typeId));
            return found ? found.name : typeId;
        })
        .join(', ');
}

function renderOrderButton({template, direction, disabled}) {
    const icon = direction === 'up' ? 'expand_less' : 'expand_more';
    const label = direction === 'up' ? 'Move template up' : 'Move template down';
    const disabledAttr = disabled ? ' disabled' : '';
    return `
        <button type="button" class="move-template-${direction}-btn inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-50 hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)] disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-400" data-id="${attrValue(template.id)}" title="${label}" aria-label="${label}"${disabledAttr}>
            <span class="material-symbols-outlined pointer-events-none text-[18px]">${icon}</span>
        </button>
    `;
}

function renderTemplateRow(template, index, templates) {
    const itemCount = template.items ? Object.keys(template.items).length : 0;
    const name = template.name || 'Unnamed Template';
    const isFirst = index === 0;
    const isLast = index === templates.length - 1;
    const manualLabel = template.isManual ?
        '<p class="text-xs text-gray-500">Manual append only</p>' :
        '';

    return `
        <div class="checklist-template-row flex items-start gap-3 rounded-md border border-gray-200 bg-white p-3 transition-colors hover:bg-gray-50" data-template-row="${attrValue(template.id)}">
            <div class="checklist-template-order flex shrink-0 flex-col items-center gap-0.5 pt-0.5" aria-label="Template order controls">
                ${renderOrderButton({template, direction: 'up', disabled: isFirst})}
                ${renderOrderButton({template, direction: 'down', disabled: isLast})}
            </div>
            <div class="checklist-template-content min-w-0 flex-1">
                <div class="checklist-template-title-row flex items-start justify-between gap-3">
                    <h5 class="truncate text-sm font-semibold text-gray-800">${escapeHTML(name)}</h5>
                    <span class="shrink-0 whitespace-nowrap text-xs font-semibold text-blue-600">${itemCount} ${itemCount === 1 ? 'item' : 'items'}</span>
                </div>
                <div class="mt-1 space-y-0.5 text-xs leading-5 text-gray-500">
                    <p><span class="font-medium text-gray-400">Boards:</span> ${escapeHTML(formatAllowedEntities(template.allowedEntities))}</p>
                    <p><span class="font-medium text-gray-400">Types:</span> ${escapeHTML(formatLinkedTypes(template.linkedTypes))}</p>
                    ${manualLabel}
                </div>
            </div>
            <button type="button" class="open-template-builder-btn checklist-template-edit inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)]" data-id="${attrValue(template.id)}" title="Edit template" aria-label="Edit template">
                <span class="material-symbols-outlined pointer-events-none text-[18px]">edit</span>
            </button>
            <button type="button" class="delete-template-btn checklist-template-delete inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-red-600 focus:outline-none focus-visible:text-red-600" data-id="${attrValue(template.id)}" title="Delete template" aria-label="Delete template">
                <span class="material-symbols-outlined pointer-events-none text-[18px]">delete</span>
            </button>
        </div>
    `;
}

async function updateTemplateOrder(templateId, direction) {
    const templates = getOrderedTemplates();
    const currentIndex = templates.findIndex((template) => template.id === templateId);
    const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= templates.length) {return;}

    const currentTemplate = templates[currentIndex];
    const nextTemplate = templates[nextIndex];
    const currentOrder = getTemplateOrder(currentTemplate, currentIndex);
    const nextOrder = getTemplateOrder(nextTemplate, nextIndex);
    const auditPayload = {
        updatedAt: serverTimestamp(),
        updatedBy: state.currentUser?.uid || null,
    };

    const batch = writeBatch(db);
    batch.update(doc(db, 'checklist_templates', currentTemplate.id), {
        sortOrder: nextOrder,
        ...auditPayload,
    });
    batch.update(doc(db, 'checklist_templates', nextTemplate.id), {
        sortOrder: currentOrder,
        ...auditPayload,
    });
    await batch.commit();
}

/**
 * Renders the checklist templates settings tab.
 *
 * @param {HTMLElement} container The tab container element.
 * @returns {void}
 */
export function renderChecklistsTab(container) {
    const templates = getOrderedTemplates();

    let rowsHtml;
    if (templates.length === 0) {
        rowsHtml = `<div class="rounded-md border border-dashed border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">No checklist templates have been created yet.</div>`;
    } else {
        rowsHtml = templates.map((template, index) =>
            renderTemplateRow(template, index, templates)).join('');
    }

    container.innerHTML = `
        <div class="max-w-5xl pb-20">
            <div class="bg-white p-6 rounded-lg shadow-md mb-8">
                <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 border-b pb-4 border-gray-100">
                    <div class="mb-4 md:mb-0 md:mr-6">
                        <h4 class="text-xl font-bold text-gray-800">Checklist Templates</h4>
                        <p class="text-sm text-gray-600 mt-1 max-w-2xl">Create reusable checklists that users can automatically apply to board items based on their Category/Type.</p>
                    </div>
                     <button id="add-new-template-btn" class="inline-flex h-9 items-center justify-center rounded-md bg-[var(--dfc-brand-primary)] px-4 text-sm font-semibold text-gray-900 transition-colors hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none whitespace-nowrap shrink-0">
                        <span class="material-symbols-outlined mr-2 text-[18px]">add</span>
                        New Template
                    </button>
                </div>

                <div id="checklist-template-list" class="space-y-2">
                    ${rowsHtml}
                </div>
            </div>
        </div>
    `;
}

/**
 * Handles checklist settings tab button actions.
 *
 * @param {HTMLElement} target The clicked element.
 * @returns {boolean} True when the click was handled.
 */
export function handleChecklistSettingsButtonClick(target) {
    const moveUpButton = target.closest('.move-template-up-btn');
    const moveDownButton = target.closest('.move-template-down-btn');
    if (moveUpButton || moveDownButton) {
        const button = moveUpButton || moveDownButton;
        const direction = moveUpButton ? 'up' : 'down';
        const templateId = button.dataset.id;
        button.disabled = true;
        updateTemplateOrder(templateId, direction).catch((err) => {
            console.error('Failed to update checklist template order:', err);
            button.disabled = false;
            openConfirmationModal(
                'Error',
                `Could not update checklist template order. ${err.message}`,
                () => {},
                false,
                'OK',
            );
        });
        return true;
    }

    if (target.closest('.delete-template-btn')) {
        const btn = target.closest('.delete-template-btn');
        const templateId = btn.dataset.id;
        openConfirmationModal('Delete Template', 'Are you sure you want to permanently delete this template? Any items currently using it will not be affected.', async () => {
            try {
                await handleDeleteItem(templateId, 'checklist_templates');
            } catch (err) {
                console.error("Failed to delete template", err);
                openConfirmationModal("Error", `Could not delete template. ${err.message}`, () => {}, false, 'OK');
            }
        }, true, 'Delete Template');
        return true;
    }

    if (target.closest('.open-template-builder-btn') || target.closest('#add-new-template-btn')) {
        const btn = target.closest('.open-template-builder-btn');
        const templateId = btn ? btn.dataset.id : null;
        let initialData;

        if (templateId) {
            initialData = (appData.checklistTemplates || []).find((template) => template.id === templateId);
        } else {
            const maxOrder = appData.checklistTemplates?.length > 0 ? Math.max(...appData.checklistTemplates.map((template) => template.sortOrder || 0)) : 0;
            initialData = {
                sortOrder: maxOrder + 1,
                allowedEntities: ['all'],
                linkedTypes: [],
                isManual: false,
            };
        }

        openChecklistTemplateForm(initialData);
        return true;
    }

    return false;
}

/**
 * Registers a listener that refreshes the checklist tab after template updates.
 *
 * @param {Function} renderCurrentTabContent Settings tab rerender callback.
 * @param {Function} getCurrentSettingsTab Callback returning the active tab id.
 * @returns {void}
 */
export function bindChecklistTemplateUpdates(renderCurrentTabContent, getCurrentSettingsTab) {
    window.addEventListener('checklistTemplatesUpdated', () => {
        if (state.currentView === 'settings' && getCurrentSettingsTab?.() === 'checklists') {
            renderCurrentTabContent?.();
        }
    });
}
